/**
 * Phase F-finale-7 Slice 7b.4a — IA seat-management Zones API client.
 *
 * Wraps the real-auth seating Zone surface (eduviaos-backend
 * `app/domains/seating/routes.py`, all `manage_zones`-guarded):
 *
 *   GET    /api/zones            (list, branch/active filters + paging)
 *   POST   /api/zones            (create)
 *   PATCH  /api/zones/{zone_id}  (update)
 *
 * Tenant + actor are resolved BE-side from the JWT `AuthContext`; none of
 * those are client-trusted. Reads + writes both require `MANAGE_ZONES`
 * (enforced server-side via the seating `_ADMIN_GUARD`; the FE additionally
 * gates write affordances via `useCanEditSeating`, the same hook the Slice
 * 7b.1 config editor uses).
 *
 * The wire shapes mirror `app/domains/seating/schemas.py` (`ZoneResponse`,
 * `CreateZoneRequest`, `UpdateZoneRequest`, `ZoneListResponse`) exactly —
 * any field-shape change on the BE must land here too (Iron Rule #1/#11).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiError } from "@eduviaos/shared";
import { api } from "@/lib/api";

/** Room override of the centre seat rule. `inherit` uses the centre default. */
export type ZoneAssignmentMode = "inherit" | "none" | "zone_only" | "dynamic" | "fixed" | "mixed";

export const ZONE_ASSIGNMENT_LABELS: Record<ZoneAssignmentMode, string> = {
  inherit: "Centre default",
  none: "No seating",
  zone_only: "Room only",
  dynamic: "First come",
  fixed: "Permanent",
  mixed: "Learner plans",
};

/** Mirror of `ZoneResponse` (eight + provenance/timestamp fields). */
export interface Zone {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  name: string;
  slug: string;
  description: string | null;
  capacity: number;
  facilities: string[];
  /** Absent on rows saved before per-room rules — treat as the centre default. */
  assignment_mode?: ZoneAssignmentMode;
  is_active: boolean;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ZoneListResponse {
  items: Zone[];
  total: number;
}

/** Mirror of `CreateZoneRequest`. `slug` is immutable post-create. */
export interface CreateZoneInput {
  name: string;
  slug: string;
  description?: string | null;
  branch_id?: string | null;
  capacity?: number;
  facilities?: string[];
  assignment_mode?: ZoneAssignmentMode;
  metadata?: Record<string, unknown>;
}

/** Mirror of `UpdateZoneRequest` — every field optional (PATCH semantics). */
export interface UpdateZoneInput {
  name?: string;
  description?: string | null;
  capacity?: number;
  facilities?: string[];
  assignment_mode?: ZoneAssignmentMode;
  is_active?: boolean;
  metadata?: Record<string, unknown>;
}

export interface ZoneListFilters {
  branch_id?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}

// ── React Query hooks ─────────────────────────────────────────────────

export const zoneKeys = {
  all: ["ia", "seating", "zones"] as const,
  list: (filters?: ZoneListFilters) => [...zoneKeys.all, "list", filters ?? {}] as const,
};

function buildZonesQuery(filters?: ZoneListFilters): string {
  const search = new URLSearchParams();
  if (!filters) return "";
  if (filters.branch_id) search.set("branch_id", filters.branch_id);
  if (filters.is_active !== undefined) search.set("is_active", String(filters.is_active));
  if (filters.limit !== undefined) search.set("limit", String(filters.limit));
  if (filters.offset !== undefined) search.set("offset", String(filters.offset));
  return search.toString();
}

export function useZones(filters?: ZoneListFilters) {
  const qs = buildZonesQuery(filters);
  return useQuery<ZoneListResponse>({
    queryKey: zoneKeys.list(filters),
    queryFn: () => api.get<ZoneListResponse>(`/api/zones${qs ? `?${qs}` : ""}`),
    staleTime: 15_000,
  });
}

export function useCreateZone() {
  const qc = useQueryClient();
  return useMutation<Zone, ApiError, CreateZoneInput>({
    mutationKey: ["ia", "seating", "zones", "create"],
    mutationFn: (body) => api.post<Zone>("/api/zones", body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: zoneKeys.all });
    },
  });
}

export interface UpdateZoneVars {
  id: string;
  body: UpdateZoneInput;
}

export function useUpdateZone() {
  const qc = useQueryClient();
  return useMutation<Zone, ApiError, UpdateZoneVars>({
    mutationKey: ["ia", "seating", "zones", "update"],
    mutationFn: ({ id, body }) => api.patch<Zone>(`/api/zones/${id}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: zoneKeys.all });
    },
  });
}

// ── Pure helpers (write-body normalisation) ────────────────────────────

/** Trim a free-text value to `null` when empty (BE treats "" as unset). */
export function trimmedOrNull(value: string): string | null {
  const t = value.trim();
  return t.length > 0 ? t : null;
}

/** Split a comma / newline separated facilities string into a clean list. */
export function parseFacilities(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((f) => f.trim())
    .filter((f) => f.length > 0);
}
